import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export const KIT_KEY = ["kit"];

export function useKitQuery() {
  return useQuery({ queryKey: KIT_KEY, queryFn: async () => (await api.get("/kit")).data, refetchOnMount: "always" });
}

export function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const plural = (n) => `${n} pigment${n === 1 ? "" : "s"}`;

export function useKitActions() {
  const qc = useQueryClient();
  const refresh = useCallback(() => qc.invalidateQueries({ queryKey: KIT_KEY }), [qc]);

  const attempt = useCallback(async (fn, failMsg) => {
    try {
      return await fn();
    } catch (e) {
      refresh();
      toast.error(`${failMsg}: ${errMsg(e)}`, { action: { label: "Retry", onClick: () => attempt(fn, failMsg) } });
      return null;
    }
  }, [refresh]);

  const offerUndo = useCallback((removed, label) => {
    if (!removed?.length) return;
    toast(`${label} removed from your kit`, {
      duration: 8000,
      action: {
        label: "Undo",
        onClick: () => attempt(async () => {
          await api.post("/kit/restore", { items: removed });
          await refresh();
          toast.success(`${plural(removed.length)} restored`);
        }, "Could not restore"),
      },
    });
  }, [attempt, refresh]);

  const add = (payload, onDone) => attempt(async () => {
    const { data } = await api.post("/kit", payload);
    await refresh();
    toast.success("Added to your kit");
    onDone?.(data);
    return data;
  }, "Could not add pigment");

  const addLine = (brand, line, onDone) => attempt(async () => {
    const { data } = await api.post("/kit/line", { brand, line });
    await refresh();
    onDone?.(data);
    toast.success(data.added.length ? `Added ${plural(data.added.length)} from ${line}${data.skipped ? ` (${data.skipped} already in kit)` : ""}` : `All ${line} shades are already in your kit`);
    return data;
  }, "Could not add line");

  const addMany = (items, onDone) => attempt(async () => {
    const { data } = await api.post("/kit/bulk", { items });
    await refresh();
    toast.success(`Added ${plural(data.added.length)}${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
    onDone?.(data);
    return data;
  }, "Could not add pigments");

  const remove = (item) => attempt(async () => {
    const { data } = await api.delete(`/kit/${item.id}`);
    await refresh();
    offerUndo(data.removed, item.shade || item.line);
    return data;
  }, "Could not remove pigment");

  const removeLine = (brand, line) => attempt(async () => {
    const { data } = await api.post("/kit/line/remove", { brand, line });
    await refresh();
    offerUndo(data.removed, `${plural(data.removed.length)} from ${line}`);
    return data;
  }, "Could not remove line");

  return { add, addLine, addMany, remove, removeLine, refresh };
}
